import React from 'react';
import { SafeAreaView, View, Image, TouchableOpacity, Text, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { BottomNavBar } from '../../components/BottomNavBar';

type RootStackParamList = {
  AllData: { viewOnly?: boolean } | undefined;
  PersonalData: { viewOnly?: boolean } | undefined;
  MedicalData: { viewOnly?: boolean } | undefined;
  FinancialData: { viewOnly?: boolean } | undefined;
  PropertyData: { viewOnly?: boolean } | undefined;
  FinalWishesData: { viewOnly?: boolean } | undefined;
  FinalCareData: { viewOnly?: boolean } | undefined;
  OnlineAccountsData: { viewOnly?: boolean } | undefined;
  EmergencyContactsData: { viewOnly?: boolean } | undefined;
  ExpensesData: { viewOnly?: boolean } | undefined;
  DocumentManagementData: { viewOnly?: boolean } | undefined;
  LoyaltyPointsData: { viewOnly?: boolean } | undefined;
  PersonalInfoIntro: undefined;
  EmploymentInfoInput: undefined;
  SpouseInfoInput: undefined;
  ChildrenInfoInput: undefined;
  PetInfoInput: undefined;
  PasswordsDashboardScreen: { viewOnly?: boolean } | undefined;
  ContactsDashboardScreen: { viewOnly?: boolean } | undefined;
  ExpensesDashboardScreen: { viewOnly?: boolean } | undefined;
  DocumentsDashboardScreen: { viewOnly?: boolean } | undefined;
  LoyaltyPointDashboardScreen: { viewOnly?: boolean } | undefined;
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const categories = [
  { id: 'all', name: 'All Data', icon: require('../../assets/icons/all-tasks.png'), count: 0 },
  { id: 'personal', name: 'Personal Data', icon: require('../../assets/icons/personal.png'), count: 0 },
  { id: 'medical', name: 'Medical Data', icon: require('../../assets/icons/medical.png'), count: 0 },
  { id: 'finance', name: 'Finance Data', icon: require('../../assets/icons/financial.png'), count: 0 },
  { id: 'property', name: 'Property Data', icon: require('../../assets/icons/property.png'), count: 0 },
  { id: 'finalWishes', name: 'Final Wishes', icon: require('../../assets/icons/final-wishes.png'), count: 0 },
  { id: 'finalCare', name: 'Final Care', icon: require('../../assets/icons/final-care.png'), count: 0 },
  { id: 'passwords', name: 'Passwords', icon: require('../../assets/icons/password.png'), count: 0 },
  { id: 'contacts', name: 'Contacts', icon: require('../../assets/icons/all-contacts.png'), count: 0 },
  { id: 'expenses', name: 'Expenses', icon: require('../../assets/icons/expenses.png'), count: 0 },
  { id: 'documents', name: 'Documents', icon: require('../../assets/icons/folder.png'), count: 0 },
  { id: 'loyaltyPoints', name: 'Loyalty Points', icon: require('../../assets/icons/loyalty.png'), count: 0 },
];

const DATA_SECTIONS = [
  { id: 'personal', title: 'Personal Data', screen: 'PersonalData', icon: require('../../assets/icons/personal.png') },
  { id: 'medical', title: 'Medical Data', screen: 'MedicalData', icon: require('../../assets/icons/medical.png') },
  { id: 'financial', title: 'Financial Data', screen: 'FinancialData', icon: require('../../assets/icons/financial.png') },
  { id: 'property', title: 'Property Data', screen: 'PropertyData', icon: require('../../assets/icons/property.png') },
  { id: 'finalWishes', title: 'Final Wishes Data', screen: 'FinalWishesData', icon: require('../../assets/icons/final-wishes.png') },
  { id: 'finalCare', title: 'Final Care Data', screen: 'FinalCareData', icon: require('../../assets/icons/final-care.png') },
  { id: 'passwords', title: 'Passwords', screen: 'PasswordsDashboardScreen', icon: require('../../assets/icons/password.png') },
  { id: 'contacts', title: 'Contacts', screen: 'ContactsDashboardScreen', icon: require('../../assets/icons/all-contacts.png') },
  { id: 'expenses', title: 'Expenses', screen: 'ExpensesDashboardScreen', icon: require('../../assets/icons/expenses.png') },
  { id: 'documents', title: 'Documents', screen: 'DocumentsDashboardScreen', icon: require('../../assets/icons/folder.png') },
  { id: 'loyaltyPoints', title: 'Loyalty Points', screen: 'LoyaltyPointDashboardScreen', icon: require('../../assets/icons/loyalty.png') },
];

// Color palette matching Loyalty Points Dashboard style
const SECTION_COLORS: string[] = [
  '#6A89A7', // Personal Data (blue)
  '#FF9F5A', // Medical Data (orange)
  '#29BFC0', // Financial Data (teal)
  '#FF6B6B', // Property Data (red)
  '#4CAF50', // Final Wishes Data (green)
  '#FFD166', // Final Care Data (yellow)
  '#A259FF', // Passwords (purple)
  '#29BFC0', // Contacts (cyan)
  '#6A89A7', // Expenses (blue gray)
  '#F2994A', // Documents (orange)
  '#FF6B6B', // Loyalty Points (red)
];

export const AllDataScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute();
  const viewOnly = (route.params as { viewOnly?: boolean } | undefined)?.viewOnly === true;
  const scrollRef = React.useRef<ScrollView>(null);

  const updatedCategories = categories.map(category => ({
    ...category,
    count: 0
  }));

  // Center selected card
  React.useEffect(() => {
    const cardWidth = 130 + 15; // width + marginRight
    const paddingLeft = 20;
    const selectedIdx = updatedCategories.findIndex(cat => cat.id === 'all');
    if (scrollRef.current && selectedIdx !== -1) {
      const screenWidth = Dimensions.get('window').width;
      const offset = Math.max(0, (cardWidth * selectedIdx + paddingLeft) - (screenWidth / 2) + (cardWidth / 2));
      scrollRef.current.scrollTo({ x: offset, animated: true });
    }
  }, []);

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../assets/icons/notification.png')}
              style={styles.bellIcon}
            />
          </TouchableOpacity>
        </View>
        {viewOnly && (
          <View style={{ backgroundColor: '#FF9F5A', padding: ms(8), alignItems: 'center', margin: ms(10), borderRadius: ms(8) }}>
            <Text style={{ color: '#fff', fontWeight: 'bold' }}>View Only Mode: You are viewing as a Successor/Keyholder</Text>
          </View>
        )}
        <Text style={styles.title}>All EOL Data</Text>
        <ScrollView style={styles.sectionsScroll} contentContainerStyle={styles.sectionsGrid} showsVerticalScrollIndicator={false}>
          {DATA_SECTIONS.map((section, idx) => (
            <TouchableOpacity
              key={section.id}
              style={[styles.sectionCard, { backgroundColor: SECTION_COLORS[idx % SECTION_COLORS.length] }]}
              onPress={() => navigation.navigate(section.screen as any)}
              activeOpacity={0.85}
            >
              <View style={styles.sectionIconRow}>
                <View style={styles.sectionIconCircle}>
                  <Image source={section.icon} style={styles.sectionIcon} />
                </View>
              </View>
              <View style={styles.sectionHeaderRow}>
                <Text style={styles.sectionTitle}>{section.title}</Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(25),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  bellIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginHorizontal: ms(15),
    marginVertical: 0,
    marginTop: ms(50),
    marginBottom: ms(10),
  },
  sectionsScroll: {
    flex: 1,
    marginTop: 0,
  },
  sectionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingHorizontal: ms(10),
    paddingBottom: ms(200),
  },
  sectionCard: {
    borderRadius: ms(15),
    paddingHorizontal: ms(10),
    paddingTop: ms(10),
    paddingBottom: ms(6),
    marginBottom: ms(10),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    width: '48%',
    minHeight: s(140),
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  sectionIconRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginBottom: ms(4),
    marginTop: 0,
  },
  sectionIconCircle: {
    width: s(45),
    height: s(45),
    borderRadius: ms(27),
    backgroundColor: 'rgba(255,255,255,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sectionIcon: {
    width: s(36),
    height: s(36),
    resizeMode: 'contain',
    tintColor: undefined,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(10),
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',

  },
  editDataButton: {
    alignSelf: 'flex-end',
    backgroundColor: '#6A89A7',
    borderRadius: ms(15),
    paddingVertical: ms(6),
    paddingHorizontal: ms(16),
    marginTop: ms(10),
    marginBottom: ms(4),
  },
  editDataButtonText: {
    color: '#fff',
    fontSize: ms(14, 0.3),
    fontWeight: 'bold',
  },
  categoriesList: {
    paddingLeft: ms(20),
    marginBottom: 0,
    height: undefined,
    borderWidth: 0,
  },
  categoryCard: {
    width: s(130),
    borderRadius: ms(15),
    padding: ms(12),
    marginRight: ms(15),
    marginVertical: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  categoryIconContainer: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#F3F6F8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#29BFC0',
  },
  categoryName: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    textAlign: 'center',
  },
  tasksButtonContainer: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(10),
    paddingVertical: ms(3),
    paddingHorizontal: ms(12),
    alignSelf: 'center',
    marginTop: ms(6),
  },
  tasksButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: ms(12, 0.3),
    textAlign: 'center',
  },
});

